/*
  tt-shoplook.js — the five "Shop the look" hotspots on the homepage.

  TWO JOBS.

  1. OPEN ONE POPUP AT A TIME. The mockup expressed this as `<sc-if>` and a
     `style-hover` attribute. Neither is a browser construct, so the transplant
     shipped five `+` buttons carrying `onClick=""` over five popups that CSS
     hid permanently. Josh, 2026-08-19: "Missing the pop-up product and effects
     when pressing the + sign."

  2. PUT THE + WHERE THE PRODUCT IS, AT EVERY WIDTH. This is the part that
     cannot be done in CSS. The panel is a fixed 680px tall and the photograph
     is square, so `object-fit: cover` scales it to the panel's WIDTH and crops
     the top and bottom — and how much it crops depends on how wide the window
     is. A hotspot stored as a percentage of the panel therefore lands on a
     different part of the room at every viewport width. There is no set of
     percentages that is right at more than one width, which is why these five
     have never lined up.

     So each hotspot stores its position as a fraction of the PHOTOGRAPH
     (data-ix / data-iy, 0-1 of the original 1600x1600), and this redoes the
     cover arithmetic against the image's real natural size on load, on resize,
     and once the image itself has decoded.

  No dependencies, no framework. The markup's inline left/top percentages are
  the no-JS fallback and are overwritten here.
*/
(function () {
  'use strict';

  /* Size of a card that is not on screen yet. `hidden` is display:none, which
     measures 0x0, so it comes off behind `visibility: hidden` for one forced
     layout and the result is cached on the element. Re-measured whenever the
     cached width no longer matches, which is how the mobile rule's 180px card
     gets picked up on a resize across the breakpoint. */
  function measure(pop) {
    var wanted = pop.style.width;
    if (pop.ttBox && pop.ttBox.css === wanted && pop.ttBox.w) return pop.ttBox;
    var wasHidden = pop.hidden;
    var wasDisplay = pop.style.display;
    pop.style.visibility = 'hidden';
    pop.style.display = 'block';
    pop.hidden = false;
    var box = { w: pop.offsetWidth, h: pop.offsetHeight, css: wanted };
    pop.hidden = wasHidden;
    pop.style.display = wasDisplay;
    pop.style.visibility = '';
    if (!box.w) box.w = parseFloat(wanted) || 240;
    pop.ttBox = box;
    return box;
  }

  var PAD = 8;

  /* The breathing halo is staggered per disc. Five pulsing in unison read as a
     machine; offset, they read as an invitation. Set here rather than in CSS
     because it depends on how many hotspots the panel has - and it is NEGATIVE,
     so every disc is already mid-cycle on first paint instead of the whole set
     starting together once the page settles. */
  function stagger(spots) {
    for (var i = 0; i < spots.length; i++) {
      var dot = spots[i].querySelector('[data-tt-spot-dot]');
      if (dot) dot.style.setProperty('--tt-spot-delay', -(i * 560) + 'ms');
    }
  }

  function layout(panel) {
    var img = panel.querySelector('img');
    var spots = panel.querySelectorAll('[data-tt-spot]');
    if (!img || !spots.length) return;
    stagger(spots);

    var nw = img.naturalWidth;
    var nh = img.naturalHeight;
    /* Not decoded yet. The load handler below runs this again. */
    if (!nw || !nh) return;

    var pw = panel.clientWidth;
    var ph = panel.clientHeight;
    if (!pw || !ph) return;

    /* `cover`: scale by whichever axis needs the most, then centre the
       overflow. Everything below is in panel pixels. */
    var scale = Math.max(pw / nw, ph / nh);
    var dw = nw * scale;
    var dh = nh * scale;
    var ox = (pw - dw) / 2;
    var oy = (ph - dh) / 2;

    for (var i = 0; i < spots.length; i++) {
      var s = spots[i];
      var fx = parseFloat(s.getAttribute('data-ix'));
      var fy = parseFloat(s.getAttribute('data-iy'));
      if (isNaN(fx) || isNaN(fy)) continue;

      var x = ox + fx * dw;
      var y = oy + fy * dh;
      s.style.left = x + 'px';
      s.style.top = y + 'px';

      /* WHICH WAY THE CARD OPENS. Both are measured rather than guessed at a
         breakpoint, because the panel size and the card size are both free to
         change - and the panel is `overflow: hidden`, so a card that opens the
         wrong way is not merely awkward, it is cut in half.

         The card is display:none until it opens, which is 0x0 to measure, so it
         is revealed behind `visibility: hidden` once and the size cached. */
      var pop = s.querySelector('[data-tt-spot-pop]');
      if (pop) {
        var box = measure(pop);
        var flip = x + 36 + box.w > pw - 16;
        s.classList.toggle('tt-spot--flip', flip);
        /* Sideways, clamped the same way (Josh, 2026-10-17: on a phone the cards ran off both
           edges). Opening right or flipped left, a 180px card on a 358px panel can fit neither
           side of a dot near the middle, so it slides to stay inside, PAD from each edge. */
        var left = flip ? -36 - box.w : 36;
        var lmin = PAD - x;
        var lmax = pw - PAD - box.w - x;
        left = lmax < lmin ? lmin : Math.min(Math.max(left, lmin), lmax);
        pop.style.setProperty('left', Math.round(left) + 'px', 'important');
        pop.style.right = 'auto';
        /* CLAMPED, not flipped. The panel is `overflow: hidden`, so a card that
           does not fit is not awkward - it is cut in half. The mockup opens
           every card upward from the dot's baseline, which is right for a
           hotspot low in the frame and loses the whole photograph off the top
           edge for one high in it.

           Preferred position is the mockup's; it then slides to whatever keeps
           all four sides inside the panel. Written as a spot-relative `top`
           because the hotspot is the positioned parent. */
        var want = -box.h;
        var lo = PAD - y;
        var hi = ph - PAD - box.h - y;
        var top = hi < lo ? lo : Math.min(Math.max(want, lo), hi);
        pop.style.top = Math.round(top) + 'px';
        pop.style.bottom = 'auto';
      }

      /* Off the cropped area entirely — hide rather than pin it to an edge and
         point at the wrong thing. Happens on a very tall, narrow panel. */
      s.hidden = x < 0 || x > pw || y < 0 || y > ph;
    }
  }

  function close(panel) {
    var open = panel.querySelectorAll('.tt-spot.is-open');
    for (var i = 0; i < open.length; i++) {
      open[i].classList.remove('is-open');
      var dot = open[i].querySelector('[data-tt-spot-dot]');
      if (dot) dot.setAttribute('aria-expanded', 'false');
      /* `display` is set outright, both ways. The card carries the mockup's
         inline style, and an inline `display: none` outranks every selector in
         a stylesheet - so a `.is-open` rule cannot reveal it and the attribute
         alone cannot either. Same trap as the specs strip on the PDP. */
      var pop = open[i].querySelector('[data-tt-spot-pop]');
      if (pop) {
        /* Fade OUT first, then take it out of the layout when the transition
           has run. Setting display straight to none would cut the animation
           dead - Josh, 2026-08-19: "Give me a smoother fade in out animation." */
        pop.classList.remove('is-shown');
        var el = pop;
        window.setTimeout(function () {
          if (el.classList.contains('is-shown')) return;   /* reopened meanwhile */
          el.hidden = true;
          el.style.display = 'none';
        }, 260);
      }
    }
  }

  function open(panel, spot) {
    close(panel);
    spot.classList.add('is-open');
    var dot = spot.querySelector('[data-tt-spot-dot]');
    if (dot) dot.setAttribute('aria-expanded', 'true');
    var pop = spot.querySelector('[data-tt-spot-pop]');
    if (pop) {
      pop.hidden = false;
      pop.style.display = 'block';
      /* TWO FRAMES, and they are load-bearing. `display` cannot be
         transitioned, so the card has to be in the layout before anything can
         animate from its hidden state - and the browser needs to have painted
         that state first. One frame to apply display, one to change opacity.
         With a single frame the two changes coalesce and the card just appears,
         which is the thing being fixed. Same trick as tt-reveal.js. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { pop.classList.add('is-shown'); });
      });
    }

    /* The ring the mockup drew and never fired. An animation does not replay
       while its class is already on, so it comes off and goes back on across a
       reflow read. */
    spot.classList.remove('is-pulsing');
    void spot.offsetWidth;
    spot.classList.add('is-pulsing');
  }

  function wire(panel) {
    if (panel.ttShoplook) return;
    panel.ttShoplook = true;

    panel.addEventListener('click', function (ev) {
      var dot = ev.target.closest ? ev.target.closest('[data-tt-spot-dot]') : null;
      if (dot) {
        ev.preventDefault();
        var spot = dot.closest('[data-tt-spot]');
        if (!spot) return;
        if (spot.classList.contains('is-open')) close(panel);
        else open(panel, spot);
        return;
      }
      /* A click inside an open card is not a dismissal — the View link lives
         there. Anything else on the photograph closes. */
      if (!(ev.target.closest && ev.target.closest('[data-tt-spot-pop]'))) close(panel);
    });

    /* The disc is a span carrying role="button" - the mockup draws a span and
       swapping in a real <button> brought its own box, font and baseline with
       it. Enter and Space are what a button would have given for free. */
    panel.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Enter' && ev.key !== ' ' && ev.key !== 'Spacebar') return;
      var dot = ev.target.closest ? ev.target.closest('[data-tt-spot-dot]') : null;
      if (!dot) return;
      ev.preventDefault();
      var spot = dot.closest('[data-tt-spot]');
      if (!spot) return;
      if (spot.classList.contains('is-open')) close(panel);
      else open(panel, spot);
    });

    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') close(panel);
    });

    document.addEventListener('click', function (ev) {
      if (!panel.contains(ev.target)) close(panel);
    });

    var img = panel.querySelector('img');
    if (img) {
      if (img.complete) layout(panel);
      img.addEventListener('load', function () { layout(panel); });
    }
    layout(panel);

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () { layout(panel); }, 80);
    });
  }

  function boot() {
    var panels = document.querySelectorAll('.shoplook');
    for (var i = 0; i < panels.length; i++) wire(panels[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
